import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { disconnect, listExternalConnections, type ProviderStatus, type RemoteRevocation } from "../../../api/externalConnectionsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import AnchoredTooltip from "../../../ui/overlays/AnchoredTooltip";
import { useAnchoredTooltip } from "../../../ui/overlays/useAnchoredTooltip";
import HelpArticleLink from "../../help/HelpArticleLink";
import { connectAndReturnHere } from "./connectProvider";

/**
 * Profile → Connections (ADR-0035, built provisionally): the person's own connections to
 * outside services such as GitHub, last on the Account page and condensed to one line per
 * service, its status and its action (F119, P-B). What a connection is and does is in the
 * help article the title links to (P-C); the consequences of an action show on hover or
 * focus, through the shared anchored tooltip.
 */
/** What a disconnect reported, for the provider it was about. */
interface DisconnectReport {
  readonly provider: string;
  readonly revocation: RemoteRevocation;
}

export default function ExternalConnections() {
  const { t } = useTranslation();
  const headingId = useId();
  const load = useCallback((signal: AbortSignal) => listExternalConnections(signal), []);
  const { state, reload, refresh } = useLoad(load);
  // The section, not the row, keeps what the last disconnect reported: the rows go when the
  // list fails to load again, and the warning to withdraw access by hand must stay.
  const [report, setReport] = useState<DisconnectReport | null>(null);
  const disconnected = (next: DisconnectReport) => { setReport(next); refresh(); };
  return (
    <section aria-labelledby={headingId} className="mt-8 border-t pt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h2 id={headingId} className="app-title text-lg font-semibold">{t("profile.connections.title")}</h2>
        <HelpArticleLink article="githubConnection" />
      </div>
      {state.status === "failed" ? <ErrorNotice error={state.error} onRetry={reload} className="mt-3" /> : null}
      {state.status === "loaded" ? (
        <ul className="app-divide-y mt-2">
          {state.value.map((status) => <ProviderRow key={status.provider} status={status} onDisconnected={disconnected} />)}
        </ul>
      ) : null}
      {report === null ? null : (
        <StatusMessage tone={report.revocation === "revoked" ? "success" : "warning"} className="mt-2">
          {t(`profile.connections.disconnected.${report.revocation}`, { provider: report.provider })}
        </StatusMessage>
      )}
    </section>
  );
}

function ProviderRow({ status, onDisconnected }: { status: ProviderStatus; onDisconnected: (report: DisconnectReport) => void }) {
  const { t } = useTranslation();
  const submission = useSubmission();
  const provider = status.displayName;
  const connection = status.connection?.state === "revoked" ? null : status.connection;
  const busy = submission.state.status === "submitting";
  const connect = () => { submission.submit(() => connectAndReturnHere(status.provider)); };
  const tooltip = useAnchoredTooltip<string>();
  const hintId = useId();
  // What an action does, on hover or focus (P-C); screen readers hear it as its description.
  const hinted = (key: "installHint" | "disconnectHint") => ({
    "aria-describedby": `${hintId}-${key}`,
    ...tooltip.hoverProps(t(`profile.connections.${key}`)),
    ...tooltip.focusProps(t(`profile.connections.${key}`)),
  });

  // Not set up on the server: no action here, so the line says why and who can set it up (P-B).
  const line = status.off !== null ? t(`profile.connections.off.${status.off}`)
    : connection?.state === "active" ? t("profile.connections.connectedAs", { login: connection.accountLogin })
    : connection?.state === "needs_reconnect" ? t("profile.connections.needsReconnect", { provider })
    : t("profile.connections.notConnected");

  return (
    <li className="py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium">{provider}</span>
        <span className="app-help-text min-w-0 flex-1 text-sm">{line}</span>
        {status.off === null && connection?.state === "active" && status.installationUrl !== null ? (
          <>
            <a className="app-link text-sm underline" href={status.installationUrl} target="_blank" rel="noopener noreferrer"
              {...hinted("installHint")}>{t("profile.connections.install")}</a>
            <span id={`${hintId}-installHint`} className="sr-only">{t("profile.connections.installHint")}</span>
          </>
        ) : null}
        {status.off === null && connection?.state !== "active" ? (
          <AppButton size="sm" variant="primary" disabled={busy} onClick={connect}>
            {t(connection === null ? "profile.connections.connect" : "profile.connections.connectAgain", { provider })}
          </AppButton>
        ) : null}
        {status.off === null && connection !== null ? (
          <>
            <AppButton size="sm" variant="secondary" disabled={busy} {...hinted("disconnectHint")}
              onClick={() => {
                submission.submit(async () => { onDisconnected({ provider, revocation: await disconnect(connection.id) }); });
              }}>
              {t("profile.connections.disconnect")}
            </AppButton>
            <span id={`${hintId}-disconnectHint`} className="sr-only">{t("profile.connections.disconnectHint")}</span>
          </>
        ) : null}
      </div>
      {tooltip.shown === null ? null : <AnchoredTooltip anchor={tooltip.shown.anchor} className="w-64">{tooltip.shown.content}</AnchoredTooltip>}
      {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} /> : null}
    </li>
  );
}
